import {metadaApi} from '@dms/api/base';

import {
  DeleteSpacesRequest,
  DeleteSpacesRequestSchema,
  DeleteSpacesResponse,
  DeleteSpacesResponseSchema,
  GetSpaceRequest,
  GetSpaceRequestSchema,
  GetSpaceResponse,
  GetSpaceResponseSchema,
  GetSpacesRequest,
  GetSpacesRequestSchema,
  GetSpacesResponse,
  GetSpacesResponseSchema,
  PatchSpaceRequest,
  PatchSpaceRequestSchema,
  PatchSpaceResponse,
  PatchSpaceResponseSchema,
  PostSpaceRequest,
  PostSpaceRequestSchema,
  PostSpaceResponse,
  PostSpaceResponseSchema,
  SetSpacesAsActiveRequest,
  SetSpacesAsActiveRequestSchema,
  SetSpacesAsActiveResponse,
  SetSpacesAsActiveResponseSchema,
  SetSpacesAsInactiveRequest,
  SetSpacesAsInactiveRequestSchema,
  SetSpacesAsInactiveResponse,
  SetSpacesAsInactiveResponseSchema,
} from './types';

export const metadaWarehouseSpaceApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getSpaces: build.query<GetSpacesResponse, GetSpacesRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/space`,
        method: 'GET',
        params: {warehouseId: queryArg?.warehouseId},
        queryArg,
      }),
      providesTags: ['spaces'],
      extraOptions: {
        requestSchema: GetSpacesRequestSchema,
        responseSchema: GetSpacesResponseSchema,
      },
    }),
    getSpace: build.query<GetSpaceResponse, GetSpaceRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/space/${queryArg.spaceId}`,
        method: 'GET',
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [{type: 'space', id: queryArg.spaceId}],
      extraOptions: {
        requestSchema: GetSpaceRequestSchema,
        responseSchema: GetSpaceResponseSchema,
      },
    }),
    postSpace: build.mutation<PostSpaceResponse, PostSpaceRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/space`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      // changing assignWarehouses also changes the spaces of the affected warehouses
      invalidatesTags: ['spaces', 'warehouse'],
      extraOptions: {
        requestSchema: PostSpaceRequestSchema,
        responseSchema: PostSpaceResponseSchema,
      },
    }),
    patchSpace: build.mutation<PatchSpaceResponse, PatchSpaceRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/space/${queryArg.spaceId}`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        'spaces',
        {type: 'space', id: queryArg.spaceId},
        // changing assignWarehouses also changes the spaces of the affected warehouses
        'warehouse',
      ],
      extraOptions: {
        requestSchema: PatchSpaceRequestSchema,
        responseSchema: PatchSpaceResponseSchema,
      },
    }),
    deleteSpaces: build.mutation<DeleteSpacesResponse, DeleteSpacesRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/space/delete`,
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['spaces', 'warehouse'],
      extraOptions: {
        requestSchema: DeleteSpacesRequestSchema,
        responseSchema: DeleteSpacesResponseSchema,
      },
    }),
    setSpacesAsActive: build.mutation<SetSpacesAsActiveResponse, SetSpacesAsActiveRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/space/set-as-active`,
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['spaces'],
      extraOptions: {
        requestSchema: SetSpacesAsActiveRequestSchema,
        responseSchema: SetSpacesAsActiveResponseSchema,
      },
    }),
    setSpacesAsInactive: build.mutation<SetSpacesAsInactiveResponse, SetSpacesAsInactiveRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/space/set-as-inactive`,
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['spaces'],
      extraOptions: {
        requestSchema: SetSpacesAsInactiveRequestSchema,
        responseSchema: SetSpacesAsInactiveResponseSchema,
      },
    }),
  }),
});

export const {
  useGetSpacesQuery,
  useLazyGetSpacesQuery,
  useGetSpaceQuery,
  useLazyGetSpaceQuery,
  usePostSpaceMutation,
  usePatchSpaceMutation,
  useDeleteSpacesMutation,
  useSetSpacesAsActiveMutation,
  useSetSpacesAsInactiveMutation,
} = metadaWarehouseSpaceApi;
